import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  PreviewFiltersDTO,
  IPreviewResultRowDTO,
  ResponseDTO,
  ResponseType,
  RunArchivingDTO,
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  ArchivingJobExperimentResultDTO,
} from 'business/models/Models';
import ArchivingService from 'business/services/ArchivingService';

export type RunArchivingRejection = {
  message: string;
  openExperiments: ArchivingJobExperimentResultDTO[];
};

export const previewArchivingThunk = createAsyncThunk<
  IPreviewResultRowDTO[],
  PreviewFiltersDTO
>('previewArchivingThunk', async (filters: PreviewFiltersDTO, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.fetchPreviewArchivingData(filters);
    if (result.responseType === ResponseType.Success) {
      return result.value;
    } else {
      return thunkApi.rejectWithValue(
        result.message || 'Failed to fetch preview archiving data',
      );
    }
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch preview archiving data');
  }
});

export const runArchivingThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  RunArchivingDTO,
  { rejectValue: RunArchivingRejection }
>('runArchivingThunk', async (request: RunArchivingDTO, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.startArchiving(request);
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue({
      message: result.message || 'Failed to start archiving job',
      openExperiments: result.value ?? [],
    });
  } catch {
    return thunkApi.rejectWithValue({
      message: 'Failed to start archiving job',
      openExperiments: [],
    });
  }
});

export const fetchActiveArchivingJobResultsThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  number | string | undefined
>('fetchActiveArchivingJobResultsThunk', async (jobId, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.fetchActiveArchivingJobResults(jobId);
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue(
      result.message || 'Failed to fetch active archiving job results',
    );
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch active archiving job results');
  }
});

export const fetchUndismissedArchivingJobResultsThunk = createAsyncThunk<
  ResponseWithValueDTOOfListOfArchivingJobExperimentResultDTO,
  void
>('fetchUndismissedArchivingJobResultsThunk', async (_, thunkApi) => {
  try {
    const archivingService = new ArchivingService();
    const result = await archivingService.fetchUndismissedArchivingJobResults();
    if (result.responseType === ResponseType.Success) {
      return result;
    }

    return thunkApi.rejectWithValue(
      result.message || 'Failed to fetch undismissed archiving job results',
    );
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch undismissed archiving job results');
  }
});

export const dismissArchivingResultsThunk = createAsyncThunk<ResponseDTO, number[]>(
  'dismissArchivingResultsThunk',
  async (experimentIds, thunkApi) => {
    try {
      const archivingService = new ArchivingService();
      const result = await archivingService.dismissArchivingResults(experimentIds);
      if (result.responseType === ResponseType.Success) {
        return result;
      }

      return thunkApi.rejectWithValue(
        result.message || 'Failed to clear archiving results',
      );
    } catch {
      return thunkApi.rejectWithValue('Failed to clear archiving results');
    }
  },
);

export const pauseArchivingThunk = createAsyncThunk<ResponseDTO, void>(
  'pauseArchivingThunk',
  async (_, thunkApi) => {
    try {
      const archivingService = new ArchivingService();
      const result = await archivingService.pauseArchiving();
      if (result.responseType === ResponseType.Success) {
        return result;
      }

      return thunkApi.rejectWithValue(result.message || 'Failed to pause archiving job');
    } catch {
      return thunkApi.rejectWithValue('Failed to pause archiving job');
    }
  },
);

export const resumeArchivingThunk = createAsyncThunk<ResponseDTO, void>(
  'resumeArchivingThunk',
  async (_, thunkApi) => {
    try {
      const archivingService = new ArchivingService();
      const result = await archivingService.resumeArchiving();
      if (result.responseType === ResponseType.Success) {
        return result;
      }

      return thunkApi.rejectWithValue(result.message || 'Failed to resume archiving job');
    } catch {
      return thunkApi.rejectWithValue('Failed to resume archiving job');
    }
  },
);

export const startOverArchivingThunk = createAsyncThunk<ResponseDTO, boolean>(
  'startOverArchivingThunk',
  async (includeArchivedFiles, thunkApi) => {
    try {
      const archivingService = new ArchivingService();
      const result = await archivingService.startOverArchiving(includeArchivedFiles);
      if (result.responseType === ResponseType.Success) {
        return result;
      }

      return thunkApi.rejectWithValue(
        result.message || 'Failed to start over archiving job',
      );
    } catch {
      return thunkApi.rejectWithValue('Failed to start over archiving job');
    }
  },
);
